import AnnualEventSessions from "../../../components/AnnualEventSessions";
import {eventLinkProperties} from '../../../services/embedded-events';
import React, { useState, useMemo, useEffect } from "react";
import { Calendar, ChevronRight, Lock } from "lucide-react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import AiContentSlot from "../../../components/AiContentSlot";
import { applyLang } from "../../../i18n";
import { useEvents } from "../../../services/EventService";
import { formatEventDate } from "../../../components/eventDate";

export default function EventMultiSession({ context }) {
    const { org, lang } = context;
    const groupeId = context.page?.eventId || context.route[1];
    const langValue = lang?.value || lang;

    // Groupes multi-session fetchés CÔTÉ CLIENT via /v1/events (modèle galeries).
    const { eventGroupes } = useEvents(context);
    const groupe = useMemo(() => {
        const g = (eventGroupes || []).find(x => x.id === groupeId);
        return g ? applyLang(g, lang) : null;
    }, [eventGroupes, groupeId, lang]);

    const sessions = useMemo(() => {
        if (!groupe) return [];
        const now = Date.now();
        return groupe.sessions
            .map(s => applyLang(s, lang))
            .filter(s => {
                const debut = s.dateDebut ? new Date(s.dateDebut).getTime() : 0;
                const fin = new Date(s.dateFin).getTime();
                return now >= debut && now <= fin;
            });
    }, [groupe, lang]);

    const [selectedId, setSelectedId] = useState(null);

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!groupe) return null;
    if (groupe.annualEventPage) return <AnnualEventSessions group={groupe} sessions={sessions} context={context}/>;

    const s = {
        container: {
            fontFamily: 'Inter, system-ui, sans-serif',
            maxWidth: '900px',
            margin: '0 auto',
            padding: isMobile ? '20px' : '60px 20px',
            color: '#1a1a1a',
        },
        title: {
            fontSize: isMobile ? '2rem' : '3rem',
            fontWeight: '850',
            letterSpacing: '-0.03em',
            margin: '0 0 12px 0',
            lineHeight: '1.05',
            color: '#0f172a',
        },
        subtitle: {
            fontSize: '1.1rem',
            color: '#64748b',
            marginBottom: '32px',
        },
        contentSlot: {
            marginBottom: '40px',
        },
        sessionCard: {
            position: 'sticky',
            top: '20px',
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            padding: '28px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
            marginBottom: '32px',
        },
        sectionLabel: {
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: '#94a3b8',
            fontWeight: 700,
            marginBottom: '16px',
        },
        sessionList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '20px',
            maxHeight: '320px',
            overflowY: 'auto',
        },
        sessionRow: (active, isSelected, isAvail) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 16px',
            border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
            borderRadius: '12px',
            backgroundColor: isSelected ? '#eff6ff' : '#fff',
            cursor: isAvail ? 'pointer' : 'not-allowed',
            opacity: isAvail ? 1 : 0.55,
            transition: 'all 0.15s',
        }),
        sessionLeft: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
        },
        sessionDate: {
            fontWeight: 700,
            fontSize: '0.95rem',
            color: '#0f172a',
        },
        sessionLabel: {
            fontSize: '0.85rem',
            color: '#64748b',
        },
        // Variante inline : utilisée dans la boucle des sessions, collée juste sous la session ouverte.
        linksBlock: {
            margin: '4px 0 8px 12px',
        },
        linkRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderRadius: '12px',
            backgroundColor: '#0f172a',
            color: '#fff',
            textDecoration: 'none',
            fontWeight: 600,
            marginBottom: '8px',
            transition: 'transform 0.15s',
        },
        emptyBox: {
            padding: '20px',
            backgroundColor: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#64748b',
        },
    };

    return (
        <div style={s.container}>
            <h1 style={s.title}>{groupe.title || groupe.label}</h1>
            {groupe.description && <p style={s.subtitle}>{groupe.description}</p>}

            {groupe.aiContentBlockId && (
                <div style={s.contentSlot}>
                    <AiContentSlot blockId={groupe.aiContentBlockId} lang={langValue} />
                </div>
            )}

            <div style={s.sessionCard}>
                <div style={s.sectionLabel}>
                    <Trans>Choisissez votre session</Trans>
                </div>

                {sessions.length === 0 ? (
                    <div style={s.emptyBox}>
                        <Trans>Aucune session disponible pour le moment.</Trans>
                    </div>
                ) : (
                    <>
                        <div style={s.sessionList}>
                            {sessions.map(sess => {
                                const isSelected = sess.id === selectedId;
                                const isAvail = sess.active;
                                return (
                                    <React.Fragment key={sess.id}>
                                        <button
                                            type="button"
                                            aria-expanded={isSelected}
                                            disabled={!isAvail}
                                            style={{...s.sessionRow(sess.active, isSelected, isAvail), width:"100%", textAlign:"start", font:"inherit"}}
                                            // Toggle : reclic sur la session sélectionnée la referme.
                                            onClick={() => isAvail && setSelectedId(isSelected ? null : sess.id)}
                                        >
                                            <span style={s.sessionLeft}>
                                                <span style={s.sessionDate}>
                                                    <Calendar size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                                                    {formatEventDate(sess, langValue)}
                                                </span>
                                                {(sess.title || sess.name) && <span style={s.sessionLabel}>{sess.title || sess.name}</span>}
                                            </span>
                                            {!isAvail ? (
                                                <Lock size={16} color="#94a3b8" />
                                            ) : (
                                                <span style={{
                                                    display: 'inline-flex',
                                                    transition: 'transform 0.2s',
                                                    transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)',
                                                }}>
                                                    <ChevronRight size={18} color={isSelected ? '#2563eb' : '#cbd5e1'} />
                                                </span>
                                            )}
                                        </button>

                                        {/* Liens inline juste sous la session ouverte plutôt qu'en bas de liste. */}
                                        {isSelected && (
                                            <div style={s.linksBlock}>
                                                {(sess.links || []).length === 0 ? (
                                                    <div style={s.emptyBox}>
                                                        <Trans>Aucune inscription disponible pour cette session.</Trans>
                                                    </div>
                                                ) : (
                                                    (sess.links || []).map((link, i) => {
                                                        const tLink = applyLang(link, lang);
                                                        return (
                                                            <a key={i} {...eventLinkProperties(tLink.url,context)} style={s.linkRow}>
                                                                <span>{tLink.name}</span>
                                                                <ChevronRight size={18} />
                                                            </a>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
